import React, { useState, useMemo } from 'react'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import FormTitle from '../../../components/form/form-title/form-title'
import Iconfont from '@/components/iconfont/iconfont'
import RemarkPopover from '../../../components/remark-popover/remark-popover'
import { Row, Column, Expaned } from '@/components/layout/layout'
import { getBEMCls } from '@/utils/classname'
import Table, { ColumnsType } from 'antd/lib/table'
import Big from 'big.js'
import LayoutInput from '../../../components/form/layout-input/layout-input'
import Button from '@/components/button/button'
import { deepClone, setObj, uniqBy } from '@/utils'
import SelectGoods, { useSelectGoods } from '../../../components/select-goods/select-goods'
import { useRequest, useMount } from 'ahooks'
import { $post } from '@/api'
import $msg from '@/utils/message'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { Spin } from 'antd'
import { IReduceDetail } from '../stock-update-detail/update-detail-info'
import StockReduceDetail from '../stock-update-detail/stock-update-detail'
import { Goods } from '@/model/types/goods'
import LossUnitPopover, { ILossUnit } from './loss-unit-popover'

interface StockUpdateFormProps {
    refresh: () => void
    inventoryId?: number
}

interface LossGoodInfo {
    goods: Goods
    loss_amount: number
    remark: string
    unit: ILossUnit | null
    stock_original: number
    stock_surplus: number
}

const StockReduceForm: React.FC<StockUpdateFormProps> = props => {
    const cls = (...arg: string[]) => getBEMCls('stock-update', ...arg)

    const [invertList, setInvertList] = useState<LossGoodInfo[]>([])
    const [orderInfo, setOrderInfo] = useState<IReduceDetail | null>(null)
    const [loading, setLoading] = useState<boolean>(!!props.inventoryId)

    const setAllData = useSetDataTogether()
    const { reduceGoods: reduceGood, initGoods } = useSelectGoods()

    const btnDisabled = useMemo(() => !invertList.length || invertList.some(list => !+list.loss_amount), [invertList])

    const getDetailReq = useRequest(
        () =>
            $post({
                url: '/warehouse/getinventorylossreportdetail',
                params: {
                    inventory_loss_id: props.inventoryId,
                },
            }),
        {
            manual: true,
            onSuccess: (res: IReduceDetail) => {
                setAllData(() => {
                    // 批量更新
                    setOrderInfo(res)
                    setInvertList(
                        res.loss_goods_info.map(s => ({
                            ...s,
                            unit: null,
                        })),
                    )
                    setLoading(false)
                })
                initGoods(res.loss_goods_info.map(g => g.goods))
            },
        },
    )

    useMount(() => {
        props.inventoryId && getDetailReq.run()
    })

    const columns: ColumnsType<LossGoodInfo> = [
        {
            title: '#',
            key: 'index',
            width: 40,
            align: 'center',
            render: (value, record, index) => index + 1,
        },
        {
            title: '商品名',
            key: 'name',
            ellipsis: true,
            dataIndex: 'goods',
            render: val => val.name,
        },
        {
            title: '上架',
            width: 70,
            dataIndex: 'goods',
            render: val => (
                <Row>
                    {val.status === 0 || val.on_mall ? (
                        <React.Fragment>
                            <span className={cls('icon')}>
                                {val.on_mall && <Iconfont name="icon2shangjiaweishangcheng" size={20} />}
                            </span>
                            <span>
                                {val.status === 0 && (
                                    <Iconfont
                                        name="icon2shangjiashouyintai"
                                        size={20}
                                        style={{
                                            marginLeft: '8px',
                                        }}
                                    />
                                )}
                            </span>
                        </React.Fragment>
                    ) : (
                        <span>
                            <Iconfont name="iconweishangjia1" size={35}></Iconfont>
                        </span>
                    )}
                </Row>
            ),
        },
        {
            title: '系统库存',
            key: 'stock_original',
            align: 'right',
            dataIndex: 'stock_original',
            render: val => val,
            width: 80,
        },
        {
            title: '报损量',
            key: 'loss_amount',
            dataIndex: 'loss_amount',
            align: 'right',
            render: (val, record, index) => (
                <Row data-align-items-center data-justify-content-flex-end>
                    <LayoutInput
                        value={val}
                        onBlur={e => {
                            setInvertList(deepClone(setObj(invertList, `[${index}].loss_amount`, e)))
                        }}
                    />
                    {record.goods.pricing_methods.length > 1 ? (
                        <LossUnitPopover
                            unit={{
                                ...record.goods.stock_unit,
                                key: record.goods.stock_unit.id,
                                sale_unit_ratio: 1,
                            }}
                            goods={record.goods}
                            onConfirm={unit => {
                                setInvertList(deepClone(setObj(invertList, `[${index}].unit`, unit)))
                            }}
                        >
                            <div
                                data-cursor-pointer
                                data-position-relative
                                data-center
                                data-width-20
                                data-height-20
                                data-font-size-14
                            >
                                {record.unit ? record.unit.name : record.goods.stock_unit.name}
                                {record.goods.pricing_methods.length > 1 && <div className="unit-check" />}
                            </div>
                        </LossUnitPopover>
                    ) : (
                        <div
                            data-cursor-pointer
                            data-position-relative
                            data-center
                            data-width-20
                            data-height-20
                            data-font-size-14
                        >
                            {record.unit ? record.unit.name : record.goods.stock_unit.name}
                        </div>
                    )}
                </Row>
            ),
        },
        {
            title: '剩余库存',
            key: 'stock_surplus',
            width: 80,
            align: 'right',
            render: (val, record) =>
                +Big(record.stock_original).minus(
                    record.unit ? +Big(record.loss_amount).times(record.unit.sale_unit_ratio) : record.loss_amount || 0,
                ),
        },
        {
            title: '备注',
            key: 'remark',
            dataIndex: 'remark',
            align: 'right',
            render: (val, record, index) => (
                <RemarkPopover
                    value={val}
                    onConfirm={e => {
                        setInvertList(deepClone(setObj(invertList, `[${index}].remark`, e)))
                    }}
                >
                    {val ? (
                        <div className={cls('remark')}>{val}</div>
                    ) : (
                        <Row data-justify-content-flex-end data-cursor-pointer data-align-items-center>
                            <div data-font-size-14 data-color-999>
                                请设置
                            </div>
                            <Iconfont data-margin-left-4 name="icon6youjiantou-20" size={16} />
                        </Row>
                    )}
                </RemarkPopover>
            ),
        },
        {
            title: '',
            key: 'reduce',
            width: 40,
            align: 'center',
            render: (val, rec, index) => (
                <div data-cursor-pointer>
                    <Iconfont
                        name="icon6yichu-20"
                        size={20}
                        data-cursor-pointer
                        onClick={() => {
                            invertList.splice(index, 1)
                            setInvertList(deepClone(invertList))
                            reduceGood(rec.goods.id)
                        }}
                    />
                </div>
            ),
        },
    ]

    const createReq = useRequest(
        params =>
            $post({
                url: props.inventoryId ? '/warehouse/editinventorylossreport' : '/warehouse/createinventorylossreport',
                params: params,
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '添加成功')
                layerManage.pop()
                props.refresh()
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const onSubmit = (submit: boolean = true) => {
        const formatData = invertList.map(list => {
            const amount = list.unit ? +Big(+list.loss_amount).times(list.unit.sale_unit_ratio) : +list.loss_amount

            return {
                goods_id: list.goods.id,
                remark: list.remark,
                loss_amount: amount,
                loss_price: +Big(amount).times(list.goods.pricing_methods[0].sale_prices[0].sale_price),
            }
        })

        createReq.run({
            inventory_loss_goods: formatData,
            submit,
            inventory_loss_id: props.inventoryId,
        })
    }

    return (
        <BaseDrawer
            isShow
            title="新建报损单"
            width={750}
            maskClosable={false}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column data-height-100p>
                <Expaned data-overflow-auto>
                    <Spin spinning={loading}>
                        {props.inventoryId && (
                            <StockReduceDetail
                                status={orderInfo?.status}
                                creator={orderInfo?.creator}
                                update_time={orderInfo?.update_time}
                            />
                        )}
                        <FormTitle title="报损清单" hasLine={false}>
                            <div
                                data-font-size-16
                                data-margin-right-16
                                className="text-btn-blue"
                                onClick={() => {
                                    showLayer(
                                        <SelectGoods
                                            filterFn={goods => !goods.on_mall && goods.status === 1}
                                            onSubmit={goods => {
                                                const formatGoods = goods.map(good => ({
                                                    goods: good,
                                                    loss_amount: 0,
                                                    remark: '',
                                                    stock_original: good.stock,
                                                    stock_surplus: good.stock,
                                                }))

                                                const setGoods = uniqBy([...invertList, ...formatGoods], 'goods.id')

                                                setInvertList(setGoods)
                                            }}
                                        />,
                                    )
                                }}
                            >
                                +添加商品
                            </div>
                        </FormTitle>
                        <div
                            style={{
                                width: 'calc(100% - 32px)',
                                marginLeft: '16px',
                            }}
                        >
                            <Table
                                rowKey={record => record.goods.id}
                                bordered
                                pagination={false}
                                columns={columns}
                                dataSource={invertList}
                                scroll={{ x: 'max-content', y: 500 }}
                            />
                        </div>
                    </Spin>
                </Expaned>
                <div className={cls('bottom')}>
                    <Row
                        data-align-items-center
                        className="text-btn-blue"
                        onClick={() => {
                            if (createReq.loading) return
                            onSubmit(false)
                        }}
                    >
                        <i
                            data-margin-right-4
                            data-font-size-20
                            className="iconfont icon6cunweicaogao-20"
                            style={{
                                fontWeight: 500,
                            }}
                        />
                        存为草稿
                    </Row>
                    <Row>
                        <Button
                            disabled={btnDisabled}
                            types="default"
                            loading={createReq.loading || void 0}
                            onClick={() => {
                                onSubmit()
                            }}
                        >
                            确认提交
                        </Button>
                    </Row>
                </div>
            </Column>
        </BaseDrawer>
    )
}

export default StockReduceForm
